/**
 * ListingTable — PC 테이블 + 모바일 카드 반응형 집합체.
 *
 * 초기화 방법:
 *   new ListingTable(wrap).init()           // 직접
 *   ListingTable.initAll()                  // 전역 자동 (GlobalApplication)
 *
 * 마크업 계약:
 *   <div data-listing-table>   ← 루트 wrap (또는 data-table-manager — 하위 호환)
 *     <table class="admin-table">
 *       <thead><tr><th>헤더</th>…</tr></thead>
 *       <tbody>
 *         <tr data-row-id="123">   ← data-id="123" 도 허용
 *           <td>…</td>
 *           <td>
 *             <!-- data-row-action 버튼 (Table 기반 제거) -->
 *             <button data-row-action
 *                     data-action-url="/console/…/123"
 *                     data-action-method="DELETE"
 *                     data-action-confirm="삭제하시겠습니까?"
 *                     data-success-action="remove-row"
 *                     data-success-toast="삭제했습니다.">삭제</button>
 *           </td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </div>
 *
 * 모바일 카드:
 *   init() 호출 시 테이블 헤더를 읽어 .admin-table-cards 를 자동 생성.
 *   (이미 생성된 경우 재생성하지 않음)
 *   카드에 data-row-id 가 붙어 removeRow() 에서 테이블 행·카드를 동시에 제거.
 *
 * 빈 상태:
 *   행 전체 제거 후 tbody 가 비면 [data-empty-state] 를 표시.
 *   [data-empty-state] 요소가 없으면 wrap에 .is-empty 클래스만 부착.
 */

import { Table } from "./Table";

export class ListingTable extends Table {
    readonly #tbody: HTMLTableSectionElement | null;
    readonly #emptyState: HTMLElement | null;

    constructor(el: HTMLElement) {
        super(el);
        this.#tbody = el.querySelector<HTMLTableSectionElement>("tbody");
        this.#emptyState = el.querySelector<HTMLElement>("[data-empty-state]");
    }

    init(): this {
        if (!this.#tbody) return this;
        this.#buildCards();
        this.bindActionButtons();
        return this;
    }

    removeRow(id: string): void {
        const row = this.#tbody?.querySelector<HTMLElement>(
            `[data-row-id="${id}"], [data-id="${id}"]`
        );
        row?.remove();
        this.el
            .querySelector<HTMLElement>(`.admin-table-card[data-row-id="${id}"]`)
            ?.remove();
        this.checkEmpty();
    }

    protected checkEmpty(): void {
        const isEmpty = !this.#tbody?.querySelector("tr");
        if (this.#emptyState) {
            this.#emptyState.hidden = !isEmpty;
        }
        this.el.classList.toggle("is-empty", isEmpty);
    }

    // ── 카드 생성 (TableManager.build() 대체) ──────────────────────────────

    #buildCards(): void {
        // index-table 컴포넌트는 .admin-index-card 를 서버에서 직접 렌더하므로 JS 카드 생성을 끈다(중복 방지).
        if (this.el.dataset.cards === "server") return;
        if (this.el.querySelector(".admin-table-cards")) return; // 이미 생성됨

        const table = this.el.querySelector<HTMLTableElement>("table");
        if (!table || !this.#tbody) return;

        const headers = Array.from(
            table.querySelectorAll<HTMLTableCellElement>("thead th")
        ).map((th) => th.textContent?.trim() ?? "");

        // 첫 번째 빈 헤더 = 체크박스 열 (카드 상단 체크로 렌더)
        const checkColIdx = headers.findIndex((h) => h === "");
        // 마지막 빈 헤더 = 액션 열 (체크박스 열과 다를 때만)
        const actionColIdx = (() => {
            for (let i = headers.length - 1; i >= 0; i--) {
                if (headers[i] === "" && i !== checkColIdx) return i;
            }
            return -1;
        })();
        // 아이콘 열: 체크박스·액션 외 빈 헤더 (없으면 -1)
        const iconColIdx = headers.findIndex(
            (h, i) => h === "" && i !== checkColIdx && i !== actionColIdx
        );

        const cards = document.createElement("div");
        cards.className = "admin-table-cards";

        Array.from(this.#tbody.querySelectorAll<HTMLTableRowElement>("tr")).forEach((tr) => {
            const cells = Array.from(tr.querySelectorAll<HTMLTableCellElement>("td"));
            const card = document.createElement("div");
            card.className = ["admin-table-card", ...Array.from(tr.classList)].join(" ");

            // data-row-id 연결 (data-id 하위 호환)
            const rowId = tr.dataset.rowId ?? tr.dataset.id ?? "";
            if (rowId) card.dataset.rowId = rowId;

            // 카드 헤더: 체크박스(왼쪽) + 아이콘(있을 때)
            const hasCheck = checkColIdx >= 0 && cells[checkColIdx];
            const hasIcon = iconColIdx >= 0 && cells[iconColIdx];
            if (hasCheck || hasIcon) {
                const cardHeader = document.createElement("div");
                cardHeader.className = "admin-table-card__header";
                if (hasCheck) {
                    const checkWrap = document.createElement("div");
                    checkWrap.className = "admin-table-card__check";
                    checkWrap.innerHTML = cells[checkColIdx].innerHTML;
                    cardHeader.appendChild(checkWrap);
                }
                if (hasIcon) {
                    const iconWrap = document.createElement("div");
                    iconWrap.className = "admin-table-card__icon";
                    iconWrap.innerHTML = cells[iconColIdx].innerHTML;
                    cardHeader.appendChild(iconWrap);
                }
                card.appendChild(cardHeader);
            }

            // 본문 행
            const body = document.createElement("div");
            body.className = "admin-table-card__body";

            headers.forEach((label, i) => {
                if (i === checkColIdx || i === iconColIdx || i === actionColIdx || !cells[i] || !label) return;
                const row = document.createElement("div");
                row.className = "admin-table-card__row";
                const labelEl = document.createElement("span");
                labelEl.className = "admin-table-card__label";
                labelEl.textContent = label;
                const valueEl = document.createElement("span");
                valueEl.className = "admin-table-card__value";
                valueEl.innerHTML = cells[i].innerHTML;
                row.appendChild(labelEl);
                row.appendChild(valueEl);
                body.appendChild(row);
            });

            card.appendChild(body);

            // 액션 셀
            if (actionColIdx >= 0 && cells[actionColIdx]) {
                const actionDiv = document.createElement("div");
                actionDiv.className = "admin-table-card__action";
                actionDiv.innerHTML = cells[actionColIdx].innerHTML;
                card.appendChild(actionDiv);
            }

            cards.appendChild(card);
        });

        this.el.appendChild(cards);
    }

    // ── 전역 자동 초기화 ────────────────────────────────────────────────────

    /** [data-listing-table], [data-table-manager] 요소를 모두 초기화. */
    static initAll(): void {
        document
            .querySelectorAll<HTMLElement>("[data-listing-table], [data-table-manager]")
            .forEach((el) => new ListingTable(el).init());
    }
}
